import React, { useState, useEffect } from "react";
import { FlashcardCollection, RecallCollection, MapCollection, User } from "../types";
import {
  FaSearch,
  FaLayerGroup,
  FaBrain,
  FaMapMarkedAlt,
  FaSdCard,
} from "react-icons/fa";
import { FaReddit, FaXTwitter, FaFacebook, FaTelegram } from "react-icons/fa6";
import { ShareButtons } from "./ShareButtons";
import "../styles_1/Dashboard.css";

interface DashboardProps {
  collections: FlashcardCollection[];
  recallSheets: RecallCollection[];
  mapSessions: MapCollection[];
  onViewCollection: (collection: FlashcardCollection) => void;
  onDeleteCollection: (collectionId: string) => void;
  onViewRecall: (sheet: RecallCollection) => void;
  onDeleteRecall: (sheetId: string) => void;
  onViewMap: (map: MapCollection) => void;
  onDeleteMap: (mapId: string) => void;
  user: User | null;
  onShowAuth: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  collections,
  recallSheets,
  mapSessions = [],
  onViewCollection,
  onDeleteCollection,
  onViewRecall,
  onDeleteRecall,
  onViewMap,
  onDeleteMap,
  user,
  onShowAuth,
}) => {
  // Removed statusFilter
  const [typeFilter, setTypeFilter] = useState<"all" | "flashcards" | "recall" | "maps">(
    "all"
  );
  const [searchQuery, setSearchQuery] = useState("");

  // --- FILTERING LOGIC (Simplified) ---
  const filteredCollections = collections.filter((collection) => {
    if (typeFilter === "recall") return false;
    return (
      collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const filteredRecallSheets = recallSheets.filter((sheet) => {
    if (typeFilter === "flashcards") return false;
    return (
      sheet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sheet.subject.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const filteredMapSessions = mapSessions.filter((map) => {
    if (typeFilter === "flashcards" || typeFilter === "recall") return false;
    return (
      map.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      map.region.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getStats = () => {
    return {
      decks: collections.length,
      recall: recallSheets.length,
      maps: mapSessions.length,
      cards: collections.reduce((sum, c) => sum + c.flashcards.length, 0),
    };
  };

  const stats = getStats();

  // --- STYLES (Inline for immediate UI fix) ---
  const statCardStyle: React.CSSProperties = {
    background: "white",
    borderRadius: "16px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
    transition: "all 0.2s ease-in-out",
    cursor: "default",
    minHeight: "140px",
    position: "relative",
    overflow: "hidden",
  };


  return (
    <div
      className="dashboard"
      style={{ padding: "30px", maxWidth: "1200px", margin: "0 auto" }}
    >
      {/* 1. HEADER & SEARCH SECTION */}
      <div style={{ marginBottom: "40px" }}>
        <h1
          style={{
            fontSize: "2.5rem",
            color: "#1e293b",
            marginBottom: "10px",
            fontWeight: 800,
          }}
        >
          Dashboard
        </h1>
        <div style={{ position: 'absolute', top: '30px', right: '30px' }}>
          <ShareButtons title="Mastering UPSC preparation with ScoreSmart Dashboard!" />
        </div>
        <p
          style={{ color: "#64748b", fontSize: "1.1rem", marginBottom: "30px" }}
        >
          Welcome back! Here is your study overview.
        </p>

        <div
          style={{
            display: "flex",
            gap: "15px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* MODERN SEARCH BAR */}
          <div
            style={{
              flex: 1,
              minWidth: "300px",
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Search your library..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "16px 20px 16px 50px",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                fontSize: "1rem",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                outline: "none",
              }}
            />
            <FaSearch
              style={{
                position: "absolute",
                left: "20px",
                color: "#94a3b8",
                fontSize: "1.2rem",
              }}
            />
            <button
              style={{
                position: "absolute",
                right: "10px",
                padding: "8px 16px",
                background: "#1e293b",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Search
            </button>
          </div>

          {/* SIMPLE TYPE FILTER */}
          <div
            style={{
              display: "flex",
              background: "#f1f5f9",
              padding: "5px",
              borderRadius: "10px",
            }}
          >
            <button
              onClick={() => setTypeFilter("all")}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                cursor: "pointer",
                background: typeFilter === "all" ? "white" : "transparent",
                color: typeFilter === "all" ? "#1e293b" : "#64748b",
                boxShadow:
                  typeFilter === "all" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              }}
            >
              All
            </button>
            <button
              onClick={() => setTypeFilter("flashcards")}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                cursor: "pointer",
                background:
                  typeFilter === "flashcards" ? "white" : "transparent",
                color: typeFilter === "flashcards" ? "#1e293b" : "#64748b",
                boxShadow:
                  typeFilter === "flashcards"
                    ? "0 2px 4px rgba(0,0,0,0.05)"
                    : "none",
              }}
            >
              Decks
            </button>
            <button
              onClick={() => setTypeFilter("recall")}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                cursor: "pointer",
                background: typeFilter === "recall" ? "white" : "transparent",
                color: typeFilter === "recall" ? "#1e293b" : "#64748b",
                boxShadow:
                  typeFilter === "recall"
                    ? "0 2px 4px rgba(0,0,0,0.05)"
                    : "none",
              }}
            >
              Recall
            </button>
            <button
              onClick={() => setTypeFilter("maps")}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                cursor: "pointer",
                background: typeFilter === "maps" ? "white" : "transparent",
                color: typeFilter === "maps" ? "#1e293b" : "#64748b",
                boxShadow:
                  typeFilter === "maps"
                    ? "0 2px 4px rgba(0,0,0,0.05)"
                    : "none",
              }}
            >
              Maps
            </button>
          </div>
        </div>
      </div>

      {/* 2. STATS CARDS (Horizontal Layout + Red Hover) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "25px",
          marginBottom: "40px",
        }}
      >
        {/* STAT 1: DECKS */}
        <div
          className="stat-hover-card"
          style={statCardStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#ef4444";
            e.currentTarget.style.transform = "translateY(-4px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#e2e8f0";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div
            style={{
              background: "#eff6ff",
              padding: "12px",
              borderRadius: "12px",
              color: "#3b82f6",
            }}
          >
            <FaLayerGroup size={24} />
          </div>
          <div>
            <span
              style={{ fontSize: "2.5rem", fontWeight: 800, color: "#1e293b" }}
            >
              {stats.decks}
            </span>
            <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
              Active Decks
            </p>
          </div>
        </div>

        {/* STAT 2: RECALL SHEETS */}
        <div
          className="stat-hover-card"
          style={statCardStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#ef4444";
            e.currentTarget.style.transform = "translateY(-4px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#e2e8f0";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div
            style={{
              background: "#f3e8ff",
              padding: "12px",
              borderRadius: "12px",
              color: "#8b5cf6",
            }}
          >
            <FaBrain size={24} />
          </div>
          <div>
            <span
              style={{ fontSize: "2.5rem", fontWeight: 800, color: "#1e293b" }}
            >
              {stats.recall}
            </span>
            <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
              Recall Sheets
            </p>
          </div>
        </div>

        {/* STAT 3: TOTAL CARDS */}
        <div
          className="stat-hover-card"
          style={statCardStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#ef4444";
            e.currentTarget.style.transform = "translateY(-4px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#e2e8f0";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div
            style={{
              background: "#f0fdf4",
              padding: "12px",
              borderRadius: "12px",
              color: "#10b981",
            }}
          >
            <FaSdCard size={24} /> {/* FIXED USAGE */}
          </div>
          <div>
            <span
              style={{ fontSize: "2.5rem", fontWeight: 800, color: "#1e293b" }}
            >
              {stats.cards}
            </span>
            <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
              Total Flashcards
            </p>
          </div>
        </div>

        {/* STAT 4: MAPS */}
        <div
          className="stat-hover-card"
          style={statCardStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#ef4444";
            e.currentTarget.style.transform = "translateY(-4px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#e2e8f0";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div
            style={{
              background: "#fff7ed",
              padding: "12px",
              borderRadius: "12px",
              color: "#f97316",
            }}
          >
            <FaMapMarkedAlt size={24} />
          </div>
          <div>
            <span
              style={{ fontSize: "2.5rem", fontWeight: 800, color: "#1e293b" }}
            >
              {stats.maps}
            </span>
            <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>
              Map Sessions
            </p>
          </div>
        </div>
      </div>

      {/* 3. CONTENT GRID */}
      <div>
        <h2
          style={{ fontSize: "1.5rem", color: "#334155", marginBottom: "20px" }}
        >
          Recent Materials
        </h2>

        {filteredCollections.length === 0 &&
          filteredRecallSheets.length === 0 &&
          filteredMapSessions.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px",
              background: "white",
              borderRadius: "16px",
              border: "1px dashed #cbd5e1",
            }}
          >
            <div
              style={{
                fontSize: "2rem",
                marginBottom: "15px",
                color: "#94a3b8",
              }}
            >
              📭
            </div>
            <h3 style={{ margin: "0 0 5px 0", color: "#475569" }}>
              No materials found
            </h3>
            <p style={{ color: "#94a3b8" }}>Try generating a new study set!</p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "25px",
            }}
          >
            {/* RENDER DECKS */}
            {filteredCollections.map((collection) => (
              <div
                key={collection.id}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                  position: "relative",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-3px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                  }}
                >
                  <span
                    style={{
                      background: "#eff6ff",
                      color: "#3b82f6",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    DECK
                  </span>

                  {collection.lastAttempt && (
                    <span
                      style={{
                        background: collection.lastAttempt.percentage >= 70 ? "#dcfce7" :
                          collection.lastAttempt.percentage >= 50 ? "#fef9c3" :
                            collection.lastAttempt.percentage >= 30 ? "#ffedd5" : "#fee2e2",
                        color: collection.lastAttempt.percentage >= 70 ? "#15803d" :
                          collection.lastAttempt.percentage >= 50 ? "#a16207" :
                            collection.lastAttempt.percentage >= 30 ? "#9a3412" : "#b91c1c",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      }}
                    >
                      Last: {collection.lastAttempt.percentage.toFixed(0)}% ({collection.lastAttempt.score}/{collection.lastAttempt.total})
                    </span>
                  )}

                  {collection.isLocked && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      🔒 Locked
                    </span>
                  )}
                </div>

                <h3
                  style={{
                    fontSize: "1.3rem",
                    margin: "0 0 10px 0",
                    color: "#1e293b",
                    fontWeight: 700,
                  }}
                >
                  {collection.name || "Untitled Deck"}
                </h3>

                {/* Tags row */}
                {collection.tags && collection.tags.length > 0 && (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "15px" }}>
                    {collection.tags.map((tag, i) => (
                      <span key={i} style={{ background: "#f8fafc", color: "#64748b", padding: "2px 8px", borderRadius: "4px", fontSize: "0.75rem", border: "1px solid #e2e8f0" }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "0.9rem",
                    margin: "0 0 20px 0",
                  }}
                >
                  {collection.flashcards.length} cards • Flashcards
                </p>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => onViewCollection(collection)}
                    disabled={collection.isLocked}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: collection.isLocked ? "#f1f5f9" : "#3b82f6",
                      color: collection.isLocked ? "#94a3b8" : "white",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      cursor: collection.isLocked ? "not-allowed" : "pointer",
                    }}
                  >
                    Study
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete Deck?`))
                        onDeleteCollection(collection.id);
                    }}
                    style={{
                      padding: "10px",
                      background: "white",
                      border: "1px solid #fee2e2",
                      color: "#ef4444",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {/* RENDER RECALL SHEETS */}
            {filteredRecallSheets.map((sheet) => (
              <div
                key={sheet.id}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  borderLeft: "5px solid #8b5cf6", // Purple accent
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                  position: "relative",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-3px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                  }}
                >
                  <span
                    style={{
                      background: "#f3e8ff",
                      color: "#7c3aed",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    RECALL
                  </span>
                  <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                    {new Date(sheet.dateCreated).toLocaleDateString()}
                  </span>
                </div>

                <h3
                  style={{
                    fontSize: "1.3rem",
                    margin: "0 0 10px 0",
                    color: "#1e293b",
                    fontWeight: 700,
                  }}
                >
                  {sheet.name || "Untitled Recall Sheet"}
                </h3>

                {/* Tags row for Recall */}
                {sheet.tags && sheet.tags.length > 0 && (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "15px" }}>
                    {sheet.tags.map((tag, i) => (
                      <span key={i} style={{ background: "#f5f3ff", color: "#8b5cf6", padding: "2px 8px", borderRadius: "4px", fontSize: "0.75rem", border: "1px solid #ddd6fe" }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <p
                  style={{
                    color: "#64748b",
                    fontSize: "0.9rem",
                    margin: "0 0 20px 0",
                  }}
                >
                  {sheet.items.length} Questions • {sheet.subject}
                </p>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => onViewRecall(sheet)}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: "white",
                      border: "1px solid #8b5cf6",
                      color: "#8b5cf6",
                      borderRadius: "8px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    View Sheet
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete Sheet?`)) onDeleteRecall(sheet.id);
                    }}
                    style={{
                      padding: "10px",
                      background: "white",
                      border: "1px solid #fee2e2",
                      color: "#ef4444",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {/* RENDER MAP SESSIONS */}
            {filteredMapSessions.map((map) => (
              <div
                key={map.id}
                style={{
                  background: "white",
                  borderRadius: "16px",
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  borderLeft: "5px solid #f97316", // Orange accent
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                  position: "relative",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-3px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                  }}
                >
                  <span
                    style={{
                      background: "#fff7ed",
                      color: "#ea580c",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    MAP
                  </span>
                  <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                    {new Date(map.dateCreated).toLocaleDateString()}
                  </span>
                </div>

                <h3
                  style={{
                    fontSize: "1.3rem",
                    margin: "0 0 10px 0",
                    color: "#1e293b",
                    fontWeight: 700,
                  }}
                >
                  {map.name || "Untitled Map"}
                </h3>

                {/* Tags row for Maps - Requested by user */}
                {map.tags && map.tags.length > 0 && (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "15px" }}>
                    {map.tags.map((tag, i) => (
                      <span key={i} style={{
                        background: "#fff7ed",
                        color: "#f97316",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "0.75rem",
                        border: "1px solid #ffedd5",
                        cursor: "pointer",
                        fontWeight: 600
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <p
                  style={{
                    color: "#64748b",
                    fontSize: "0.9rem",
                    margin: "0 0 20px 0",
                  }}
                >
                  {map.region} • {map.notebook}
                </p>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => onViewMap(map)}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: "white",
                      border: "1px solid #f97316",
                      color: "#f97316",
                      borderRadius: "8px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Revise Map
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete Map Session?`)) onDeleteMap(map.id);
                    }}
                    style={{
                      padding: "10px",
                      background: "white",
                      border: "1px solid #fee2e2",
                      color: "#ef4444",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
